import React from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { length } from 'redux-form-validators';
import { isEmpty, toPlainObject } from 'lodash';

import {
    FIRSTNAME_LENGTH,
    NAME_LENGTH,
    PHONE_NUMBER_LENGTH_MIN,
    PHONE_NUMBER_LENGTH_MAX,
    CITY_LENGTH,
    ADDRESS_LENGTH,
    POSTCODE_LENGTH
} from '../../../constants/form';
import { MADAME, MONSIEUR } from '../../../constants/civilities';

import { convertHtmlToReactDomWithParams } from '../../../utils/htmlUtils';

import {
    CheckboxInputFieldComponent,
    FormComponent,
    LegendFieldComponent,
    NumberInputFieldComponent,
    PhoneInputFieldComponent,
    SelectFieldComponent,
    TextInputFieldComponent
} from '../../form';
import Tooltip from '../../tooltip/Tooltip';
import LocalizedNavLink from '../../router/LocalizedNavLink';

const mapCountries = countries => {
    if (!isEmpty(countries)) {
        return Object.values(countries).map(country => {
            return {
                label: country.label,
                value: country.value
            };
        });
    }
    return {};
};

const AddressForm = ({
    formName,
    cappingError,
    isFetching,
    handleSubmit,
    merchantCardPath,
    i18n,
    i18nCommon,
    COUNTRIES,
    CIVILITIES,
    initialValues
}) => {
    return (
        <FormComponent
            form={formName}
            initialValues={initialValues}
            classNameInputContainer="grid-2 has-gutter"
            requestSubmit={isFetching}
            onSubmit={() => handleSubmit()}
            submitText={i18nCommon.VALIDATE_USER_DETAILS}
            paymentLockLabel={i18nCommon.PAYMENT_LOCKPAD_LABEL}>
            <LegendFieldComponent classNameInputContainer="full">
                {i18nCommon.WELCOME_TITLE}
            </LegendFieldComponent>

            <SelectFieldComponent
                name="civility"
                i18nFeatureForm={i18nCommon}
                label={i18nCommon.LABEL_CIVILITY}
                isRequired
                defaultOptionLabel="--"
                classNameInputContainer="one-half mr50"
                options={[
                    { label: CIVILITIES.MONSIEUR, value: MONSIEUR },
                    { label: CIVILITIES.MADAME, value: MADAME }
                ]}
            />
            <TextInputFieldComponent
                name="lastname"
                i18nFeatureForm={i18nCommon}
                placeholder={i18nCommon.LABEL_LASTNAME}
                label={i18nCommon.LABEL_LASTNAME}
                isRequired
                classNameInputContainer="one-half"
                validate={[
                    length({
                        max: NAME_LENGTH,
                        msg: format(
                            i18nCommon.TEXT_TOOLONG,
                            NAME_LENGTH,
                            NAME_LENGTH > 1 ? 's' : ''
                        )
                    })
                ]}
            />
            <TextInputFieldComponent
                name="firstname"
                i18nFeatureForm={i18nCommon}
                placeholder={i18nCommon.LABEL_FIRSTNAME}
                label={i18nCommon.LABEL_FIRSTNAME}
                isRequired
                classNameInputContainer="one-half"
                validate={[
                    length({
                        max: FIRSTNAME_LENGTH,
                        msg: format(
                            i18nCommon.TEXT_TOOLONG,
                            FIRSTNAME_LENGTH,
                            FIRSTNAME_LENGTH > 1 ? 's' : ''
                        )
                    })
                ]}
            />
            <TextInputFieldComponent
                name="address"
                i18nFeatureForm={i18nCommon}
                classNameInputContainer="full"
                placeholder={i18nCommon.LABEL_ADDRESS}
                label={i18nCommon.LABEL_ADDRESS}
                isRequired
                validate={[
                    length({
                        max: ADDRESS_LENGTH,
                        msg: i18nCommon.ADDRESS_SIZE_ERROR
                    })
                ]}
            />
            <NumberInputFieldComponent
                name="zipcode"
                i18nFeatureForm={i18nCommon}
                classNameInputContainer="one-half"
                placeholder={i18nCommon.LABEL_ZIPCODE}
                label={i18nCommon.LABEL_ZIPCODE}
                isRequired
                validate={[
                    length({
                        max: POSTCODE_LENGTH,
                        msg: i18nCommon.ZIP_CODE_SIZE_ERROR
                    })
                ]}
            />
            <TextInputFieldComponent
                name="city"
                i18nFeatureForm={i18nCommon}
                classNameInputContainer="one-half"
                placeholder={i18nCommon.LABEL_CITY}
                label={i18nCommon.LABEL_CITY}
                isRequired
                validate={[
                    length({
                        max: CITY_LENGTH,
                        msg: i18nCommon.CITY_SIZE_ERROR
                    })
                ]}
            />
            <SelectFieldComponent
                name="country"
                i18nFeatureForm={i18nCommon}
                label={i18nCommon.LABEL_COUNTRY}
                isRequired
                defaultOptionLabel={i18nCommon.LABEL_COUNTRY}
                classNameInputContainer="one-half"
                options={COUNTRIES}
            />
            <PhoneInputFieldComponent
                name="phoneNumber"
                label={i18nCommon.LABEL_PHONENUMBER}
                labelInfo={
                    <Tooltip text={i18n.PHONE_NUMBER_INFO} right isHover>
                        {i18n.PHONE_NUMBER_LABEL_INFO}
                    </Tooltip>
                }
                i18nFeatureForm={i18nCommon}
                classNameInputContainer="one-half"
                isRequired
                validate={[
                    length({
                        max: PHONE_NUMBER_LENGTH_MAX,
                        msg: format(i18nCommon.PHONENUMBER_MAX_SIZE_ERROR, PHONE_NUMBER_LENGTH_MAX)
                    }),
                    length({
                        min: PHONE_NUMBER_LENGTH_MIN,
                        msg: i18nCommon.PHONENUMBER_MIN_SIZE_ERROR
                    })
                ]}
            />
            <CheckboxInputFieldComponent
                name="saveAddress"
                options={[
                    {
                        label: i18nCommon.LABEL_SAVE_ADDRESS,
                        value: true
                    }
                ]}
                classNameInputContainer="full"
            />

            <React.Fragment>
                {cappingError && (
                    <p className="error-message">
                        {convertHtmlToReactDomWithParams(i18n.CAPPING_ERROR_MESSAGE_1)}
                        <LocalizedNavLink
                            to={`${merchantCardPath}#conditions`}
                            title={i18n.CAPPING_ERROR_MESSAGE_2}>
                            {i18n.CAPPING_ERROR_MESSAGE_2}
                        </LocalizedNavLink>
                        {i18n.CAPPING_ERROR_MESSAGE_3}
                    </p>
                )}
            </React.Fragment>
        </FormComponent>
    );
};

AddressForm.propTypes = {
    formName: PropTypes.string,
    cappingError: PropTypes.bool,
    isFetching: PropTypes.bool,
    handleSubmit: PropTypes.func,
    merchantCardPath: PropTypes.string
};

const mapStateToProps = state => {
    const user = toPlainObject(state.account.user);

    return {
        i18n: state.i18n.page.EBON,
        i18nCommon: state.i18n.feature.form,
        COUNTRIES: mapCountries(state.i18n.feature.form.COUNTRIES),
        CIVILITIES: state.i18n.feature.form.CIVILITIES,
        initialValues: {
            civility: user.civility ? user.civility : '',
            lastname: user.lastname ? user.lastname : '',
            firstname: user.firstname ? user.firstname : '',
            address: user.address ? user.address : '',
            zipcode: user.postcode ? user.postcode : '',
            city: user.city ? user.city : '',
            country: user.country ? user.country : '',
            phoneNumber: user.mobileNumber ? user.mobileNumber : ''
        }
    };
};

export default withRouter(connect(mapStateToProps)(AddressForm));



// WEBPACK FOOTER //
// ./src/components/funnel/address-step/AddressForm.js